import { createPortal } from "react-dom";
import { useEmbedding, useEmbeddedOverlay } from "../Embedding";
import React from "react";
import { useTranslation } from "react-i18next";
import { DangerButton, PrimaryButton, SecondaryButton } from "../Button";
import { translationText } from "../i18n";
import { IconAlertTriangle, IconTrash } from "../icons";

export type ConfirmDialogSize = "xx-small" | "x-small" | "small" | "medium" | "large";

export interface ConfirmDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "default";
  size?: ConfirmDialogSize;
  icon?: React.ComponentType<{ size: number; className?: string }>;
  testId?: string;
  confirming?: boolean;
}

const SIZE_CLASSES: Record<ConfirmDialogSize, string> = {
  "xx-small": "max-w-xs",
  "x-small": "max-w-sm",
  small: "max-w-md",
  medium: "max-w-lg",
  large: "max-w-2xl",
};

export function ConfirmDialog({
  isOpen,
  onConfirm,
  onCancel,
  title,
  message,
  confirmText,
  cancelText,
  variant = "default",
  size = "small",
  icon,
  testId,
  confirming = false,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const embedding = useEmbedding();
  const overlayRef = React.useRef<HTMLDivElement>(null);
  useEmbeddedOverlay(
    Boolean(embedding) && isOpen,
    () => {
      if (!confirming) onCancel();
    },
    overlayRef,
  );
  const resolvedConfirmText = confirmText ?? translationText(t("Confirm"));
  const resolvedCancelText = cancelText ?? translationText(t("Cancel"));

  if (!isOpen) return null;

  const Icon = icon || (variant === "danger" ? IconTrash : IconAlertTriangle);
  const iconColor = variant === "danger" ? "text-content-error" : "text-content-base";
  const ConfirmButton = variant === "danger" ? DangerButton : PrimaryButton;

  const dialog = (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div
        data-test-id={testId}
        className={`bg-surface-base border border-stroke-base rounded-xl shadow-xl w-full ${SIZE_CLASSES[size]}`}
      >
        <div className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <Icon size={20} className={`${iconColor} shrink-0`} />
            <h2 className="min-w-0 break-words text-xl font-semibold text-content-base">{title}</h2>
          </div>

          <p className="text-content-base">{message}</p>

          <div className="flex flex-wrap gap-3 justify-end">
            <SecondaryButton
              testId={testId ? `${testId}-cancel` : undefined}
              onClick={onCancel}
              disabled={confirming}
              className="max-w-full !whitespace-normal"
            >
              {resolvedCancelText}
            </SecondaryButton>
            <ConfirmButton
              testId={testId ? `${testId}-confirm` : undefined}
              onClick={onConfirm}
              loading={confirming}
              disabled={confirming}
              className="max-w-full !whitespace-normal"
            >
              {resolvedConfirmText}
            </ConfirmButton>
          </div>
        </div>
      </div>
    </div>
  );
  return embedding ? createPortal(dialog, embedding.portalContainer) : dialog;
}
